import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Checkbox, Spin, Alert, Input } from "antd";
import { CarOutlined, ToolOutlined, EyeOutlined } from "@ant-design/icons";
import { API_DOMAIN } from "../constants";
import { fetchMiscOptions } from "./helpers";
import { WaText } from "./MessageParts";

// Vehicle fields selectable for the supplier message — only non-empty ones render
const VEHICLE_FIELD_DEFS = [
  { key: "brand",          label: "Brand",        get: v => v.brand_name || v.brand },
  { key: "model",          label: "Model",        get: v => v.model_name || v.model },
  { key: "year",           label: "Year",         get: v => v.year },
  { key: "variant",        label: "Variant",      get: v => v.variant },
  { key: "vin",            label: "VIN",          get: v => v.vin },
  { key: "engine_code",    label: "Engine Code",  get: v => v.engine_code },
  { key: "engine_displacement", label: "Engine Displacement", get: v => v.engine_displacement },
  { key: "engine_layout",  label: "Engine Layout", get: v => v.engine_layout },
  { key: "transmission_type", label: "Transmission", get: v => v.transmission_type },
  { key: "transmission_code", label: "Transmission Code", get: v => v.transmission_code },
  { key: "drivetrain",     label: "Drivetrain",   get: v => v.drivetrain },
  { key: "fuel_type",      label: "Fuel Type",    get: v => v.fuel_type },
  { key: "country_spec",   label: "Spec",         get: v => v.country_spec },
  { key: "trim_info",      label: "Trim",         get: v => v.trim_info },
];

// Always included in the supplier message — checked, locked, not removable
const MANDATORY_VEHICLE_FIELDS = new Set(["brand", "model", "year", "vin", "country_spec"]);

const PART_FIELD_DEFS = [
  { key: "side",      label: "Side",      get: p => p.side },
  { key: "quantity",  label: "Quantity",  get: p => p.quantity },
  { key: "condition", label: "Condition", get: p => p.condition_preference },
];

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";

// WATI rejects a template send outright if any parameter value contains a
// tab, a newline, or 4+ consecutive spaces — mirrors the same restriction
// the backend already enforces server-side for every other template param.
function sanitizeAdditionalInfo(raw) {
  return raw
    .replace(/[\t\n\r]/g, " ")
    .replace(/ {4,}/g, "   ");
}

// Builds the selection payload sent to the preview and send APIs:
// { vehicle_fields: ["brand", ...], parts: [{ query_part_id, fields: ["side", ...] }], additional_info: "..." }
function buildSelection(vehicleChecked, partState, additionalInfo) {
  return {
    vehicle_fields: [...vehicleChecked],
    parts: Object.entries(partState)
      .filter(([, s]) => s.included)
      .map(([id, s]) => ({ query_part_id: Number(id), fields: [...s.fields] })),
    additional_info: (additionalInfo || "").trim(),
  };
}

export function SupplierMessageComposer({ open, taskId, task, isInternational, onSelectionChange }) {
  const vehicle = task?.vehicle || {};
  const parts = task?.query_parts || [];

  const availableVehicleFields = useMemo(
    () => VEHICLE_FIELD_DEFS.filter(d => hasValue(d.get(vehicle))),
    [vehicle]
  );

  const [vehicleChecked, setVehicleChecked] = useState(new Set());
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [partState, setPartState] = useState({}); // { [query_part_id]: { included, fields: Set } }
  const [conditionMap, setConditionMap] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const debounceRef = useRef(null);
  const initedForTaskRef = useRef(null);

  useEffect(() => {
    fetchMiscOptions("part_condition_origin").then(list => {
      setConditionMap(Object.fromEntries(list.map(o => [String(o.value), o.label])));
    });
  }, []);

  // Defaults when the task arrives: mandatory vehicle fields only; parts fully on
  useEffect(() => {
    if (!open || !task || initedForTaskRef.current === taskId) return;
    initedForTaskRef.current = taskId;
    setVehicleChecked(new Set(
      availableVehicleFields.filter(d => MANDATORY_VEHICLE_FIELDS.has(d.key)).map(d => d.key)
    ));
    setAdditionalInfo("");
    setPartState(Object.fromEntries(parts.map(p => [
      p.id ?? p.query_part_id,
      {
        included: true,
        fields: new Set(PART_FIELD_DEFS.filter(d => hasValue(d.get(p))).map(d => d.key)),
      },
    ])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task, taskId]);

  useEffect(() => {
    if (!open) { initedForTaskRef.current = null; setPreview(null); setPreviewUnavailable(false); }
  }, [open]);

  // Debounced server-side preview — same builder the send uses
  useEffect(() => {
    if (!open || !taskId || Object.keys(partState).length === 0) return;
    const selection = buildSelection(vehicleChecked, partState, additionalInfo);
    onSelectionChange?.(selection);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await axios.post(
          `${API_DOMAIN}/query_workflow/send_to_suppliers_v2/preview`,
          { task_id: taskId, is_international: isInternational, selection },
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        if (res.data?.success && res.data.data?.message != null) {
          setPreview(res.data.data.message);
          setPreviewUnavailable(false);
        } else {
          setPreviewUnavailable(true);
        }
      } catch {
        setPreviewUnavailable(true);
      } finally {
        setPreviewLoading(false);
      }
    }, 450);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId, vehicleChecked, partState, additionalInfo, isInternational]);

  const toggleVehicleField = (key) => {
    if (MANDATORY_VEHICLE_FIELDS.has(key)) return; // locked
    setVehicleChecked(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const togglePart = (id) => {
    setPartState(prev => ({ ...prev, [id]: { ...prev[id], included: !prev[id]?.included } }));
  };

  const togglePartField = (id, key) => {
    setPartState(prev => {
      const cur = prev[id];
      const fields = new Set(cur.fields);
      fields.has(key) ? fields.delete(key) : fields.add(key);
      return { ...prev, [id]: { ...cur, fields } };
    });
  };

  const allVehicleChecked = availableVehicleFields.length > 0 && availableVehicleFields.every(d => vehicleChecked.has(d.key));

  const partFieldDisplay = (def, p) => {
    const raw = def.get(p);
    if (def.key === "condition") return conditionMap[String(raw)] ?? raw;
    return raw;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%", minHeight: 0 }}>
      {/* ── Value selection ── */}
      <div style={{ flex: "0 0 auto", maxHeight: "45%", overflowY: "auto", paddingRight: 4 }}>
        {/* Vehicle */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <CarOutlined style={{ color: "#1677ff", fontSize: 13 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: "#595959" }}>Vehicle details</span>
          <div style={{ flex: 1 }} />
          <Checkbox
            checked={allVehicleChecked}
            indeterminate={vehicleChecked.size > 0 && !allVehicleChecked}
            onChange={() => setVehicleChecked(allVehicleChecked
              // "Deselect all" still keeps the mandatory fields
              ? new Set(availableVehicleFields.filter(d => MANDATORY_VEHICLE_FIELDS.has(d.key)).map(d => d.key))
              : new Set(availableVehicleFields.map(d => d.key)))}
          >
            <span style={{ fontSize: 11 }}>All</span>
          </Checkbox>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 10px", marginBottom: 12 }}>
          {availableVehicleFields.map(d => {
            const locked = MANDATORY_VEHICLE_FIELDS.has(d.key);
            return (
              <Checkbox
                key={d.key}
                checked={vehicleChecked.has(d.key)}
                disabled={locked}
                onChange={() => toggleVehicleField(d.key)}
                style={{ fontSize: 12 }}
              >
                <span style={{ fontSize: 12, color: locked ? "#262626" : undefined }}>
                  {d.label}: <strong>{String(d.get(vehicle))}</strong>
                  {locked && <span style={{ fontSize: 10, color: "#8c8c8c", marginLeft: 4 }}>🔒</span>}
                </span>
              </Checkbox>
            );
          })}
        </div>
        <div style={{ marginBottom: 12 }}>
          <Input
            size="small"
            placeholder="Additional info for this query only (not saved to the vehicle)…"
            value={additionalInfo}
            onChange={e => setAdditionalInfo(sanitizeAdditionalInfo(e.target.value))}
            maxLength={200}
            style={{ fontSize: 12 }}
          />
        </div>

        {/* Parts */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <ToolOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: "#595959" }}>Parts</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {parts.map(p => {
            const id = p.id ?? p.query_part_id;
            const st = partState[id];
            if (!st) return null;
            const availableFields = PART_FIELD_DEFS.filter(d => hasValue(d.get(p)));
            return (
              <div key={id} style={{
                border: `1px solid ${st.included ? "#d6e4ff" : "#f0f0f0"}`,
                background: st.included ? "#f7faff" : "#fafafa",
                borderRadius: 8, padding: "6px 10px",
                opacity: st.included ? 1 : 0.6,
              }}>
                <Checkbox checked={st.included} onChange={() => togglePart(id)}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{p.part_name || `Part #${id}`}</span>
                </Checkbox>
                {st.included && availableFields.length > 0 && (
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 2, paddingLeft: 24 }}>
                    {availableFields.map(d => (
                      <Checkbox
                        key={d.key}
                        checked={st.fields.has(d.key)}
                        onChange={() => togglePartField(id, d.key)}
                      >
                        <span style={{ fontSize: 11, color: "#595959" }}>
                          {d.label}: {String(partFieldDisplay(d, p))}
                        </span>
                      </Checkbox>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Supplier's view ── */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <EyeOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: "#595959" }}>Supplier's view</span>
          {previewLoading && <Spin size="small" style={{ marginLeft: 4 }} />}
        </div>
        <div style={{
          flex: 1, minHeight: 120, overflowY: "auto",
          background: "#efeae2", borderRadius: 10, padding: 12,
        }}>
          {previewUnavailable ? (
            <Alert
              type="info"
              showIcon
              style={{ fontSize: 12 }}
              message="Live preview not available yet"
              description="The message will be built server-side from your selection when sent. The preview API isn't deployed yet."
            />
          ) : preview != null ? (
            <div style={{
              background: "#d9fdd3", borderRadius: 8, padding: "8px 12px",
              fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word",
              maxWidth: "100%", boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
            }}>
              <WaText text={preview} />
            </div>
          ) : (
            <div style={{ fontSize: 12, color: "#8c8c8c", textAlign: "center", paddingTop: 30 }}>
              {previewLoading ? "Building preview…" : "Preview appears here"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
